import {ButtonTheme} from 'common/components/button/constants';

export interface BannerJsxProps {
  attrs: BannerAttrs;
  on?: {
    action?: (ev: ActionEvent) => void;
  };
}

export interface BannerAttrs {
  'banner-tracking-type'?: string /** Name of the banner for use in tracking */;
  'body-text'?: string;
  ctas?: CallToAction[];
  dropdown?: DropdownMenu;
  'footer-text'?: string;
  'header-text'?: string;
  icon?: string;
  'img-src'?: string;
  multiline?: boolean;
  'no-wrap-content'?: boolean;
  'show-close-button'?: boolean;
  size?: BannerSize;
  theme?: string;
  tooltip?: BannerTooltip;
  type?: BannerType;
}

export enum BannerAction {
  /** BannerAction event name for events that do something custom */
  Custom = `custom`,
  /** BannerAction event name for events that dismiss the banner */
  Dismiss = `dismiss`,
  /** BannerAction event name for events that bring up  a feedback form */
  Feedback = `feedback`,
  /** BannerAction event name for events that bring up help or setup,
   * as used in media/js/reports/notifications/index.ts */
  Help = `help`,
  /** BannerAction event name that links to the pricing page to upsell the user */
  Upgrade = `upgrade`,
  /** BannerAction event name that links to any management page somewhere in the app */
  Manage = `manage`,
  /** BannerAction event name that does not dismiss the banner or perform any other action.*/
  None = `none`,
  /** BannerAction event name for events that fire an email */
  Email = `email`,
  /** BannerAction event name for events that brings up the tutorial video modal */
  Tutorial = `tutorial`,
  /** BannerAction event name for domain claiming on SSO screen */
  ClaimDomain = `claimDomain`,
}

export interface BannerTooltip {
  /** URL that clicking on the text will navigate to. */
  href: string;
  /** Text to be shown at the end of the banner, hovering this text shows the tooltip. */
  text: string;
  /** Text to be shown in the tooltip on hover. */
  tooltipText: string;
}

export interface CallToAction {
  /** Action for the event that fires on CTA click.*/
  actionName: BannerAction;
  /** CTA link to open when clicked */
  href?: string;
  /** Target for CTA link href. Defaults to  '_blank', set to '_self' to open link in same page */
  hrefTarget?: string;
  /** Optional icon to show to the left of the text. */
  icon?: string;
  /** Text for the CTA button itself. */
  text: string;
  /** Optional theme for the CTA. */
  theme?: ButtonTheme;
  /** Track event to fire on click. */
  trackEventName?: string;
  /** Additional props for the track event fired above. */
  trackProps?: {[prop: string]: string};
  download?: boolean;
  elref?: string;
}

export interface DropdownMenu {
  // The type of the dropdown.
  dropdownType: DropdownType;
  // The text to render on the dropdown button.
  label: string;
  // The theme of the dropdown button.
  theme?: ButtonTheme;
}

export enum BannerTheme {
  Base = `base`,
  BaseColumn = `base-column`,
  Error = `error`,
  Info = `info`,
  InfoBlue = `info-blue`,
  Success = `success`,
  Warn = `warn`,
  Marketing = `marketing`,
  GradientBlue = `gradient-blue`,
  GradientRed = `gradient-red`,
}

export enum BannerSize {
  ExtraSmall = `extra-small`,
  Small = `small`,
  Medium = `medium`,
}

export enum BannerType {
  Embedded = `embedded`,
  Floating = `floating`,
}

/**
 * NOTE: This enum should be kept in sync with the corresponding enum in the webapp
 * code in webapp/app_api/projects/banners/views.py.
 */
export enum DropdownType {
  ProjectSelector = 1,
}

export type ActionEvent = CustomEvent<{
  actionName: string;
}>;
